import {Autocomplete, TextField} from "@mui/material";
import {FC} from "react";
import AutocompleteListBox, {AutocompleteListBoxProps} from "@/components/common/AutocompleteListBox";
import {useSSTranslation} from "@/i18n/useSSTranslation";
import {createFilterOptions} from "@mui/material";

export interface CertificationOption {
	id: string;
	label: string | any;
	value?: string;
}

interface CustomAutocompleteProps {
	options: CertificationOption[];
	value: CertificationOption | null;
	onChange: (event: any, value: CertificationOption | null) => void;
	onInputChange: (event: any, value: string, reason: string) => void;
	loading: boolean;
	hasNextPage: boolean;
	isFetching: boolean;
	loadMoreOptions: () => void;
	apiResult: { isLoading: boolean };
	label: string;
	required?: boolean;
	handleOpenModal?: (name: string) => void;
	allowAddNew?: boolean;
}

const CustomAutocomplete: FC<CustomAutocompleteProps> = ({
	options,
	value,
	onChange,
	onInputChange,
	loading,
	hasNextPage,
	isFetching,
	loadMoreOptions,
	apiResult,
	handleOpenModal,
	allowAddNew = false,
	label,
															 required = false
}) => {
	const {t} = useSSTranslation();
	const filter = createFilterOptions<CertificationOption>();

	return (
		<Autocomplete<CertificationOption, false, false, false>
			sx={{flex: 1,
				maxWidth: "450px",
				"& .MuiInputLabel-root": {
					color: "#F9F9F9B2"
				}}}
			options={options}
			value={value}
			onChange={(_event, newValue) => {
				if (allowAddNew && newValue && newValue.id === "newIdGenerated" && handleOpenModal) {
					handleOpenModal(newValue.value as string);
				} else {
					onChange(_event, newValue);
				}
			}}
			onInputChange={onInputChange}
			loading={loading}
			getOptionLabel={(option) => {
				if (typeof option === "string") return option;
				if ((option as any).inputValue) return (option as any).inputValue;
				return option.label;
			}}
			isOptionEqualToValue={(option, value) => option.id === value.id}
			filterOptions={(opts, params) => {
				const filtered = filter(opts, params);
				const {inputValue} = params;
				const isExisting = opts.some(
					(opt) => opt.label.toLowerCase() === inputValue.toLowerCase()
				);

				if (allowAddNew && !apiResult.isLoading && inputValue !== "" && !isExisting) {
					filtered.push({
						id: "newIdGenerated",
						label: `Add “${inputValue}”`,
						value: inputValue
					} as CertificationOption);
				}

				return filtered;
			}}
			ListboxComponent={AutocompleteListBox}
			ListboxProps={{
				hasNextPage,
				isFetching,
				loadMoreOptions
			} as AutocompleteListBoxProps}
			renderInput={(params) => (
				<TextField
					{...params}
					label={label}
					required={required}
					data-test-id="playlist-certificate-autocomplete-input"
					inputProps={{
						...params.inputProps,
						"aria-label": t(
							"playlist_select_certificate_label",
							"Select Certificate"
						)
					}}
					InputLabelProps={{shrink: true}}
				/>
			)}
			data-test-id="playlist-certificate-autocomplete"
			aria-label={t("playlist_select_certificate_label", "Select Certificate")}
		/>
	);
};

export default CustomAutocomplete;
